import { DialogActions, DialogTitle, FormControlLabel, IconButton, Typography } from "@mui/material";
import { Dialog, DialogContent, DialogFooter } from "../../components/components/ui/dialog";
import { ArrowLeft, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "../../common/components/Button";
import { getCall } from "../../services/apiService";
import { styled } from "@mui/system";
import Loader from "../../common/components/Loader";
import styles from "./index.module.scss";
import { BulkEmailsPopupProps, EmailTriggerOption, RadioType } from "./type";
import { BULKEMAIL } from "../../constants/textConstants";
import DataGridWithPagination from "../../common/components/DataGridWithPagination";
import { endPoints, PORTAL } from "../../constants/urlConstants";
import emailIcon from '../../assets/images/emailIcon.svg';
import closeIcon from '../../assets/images/crossIconBlue.svg';
import bulkEmailBg from '../../assets/images/bulk-mail-bg.svg'
import { notify } from "../../common/components/ToastMessage";



const Radio = ({ value, checked, onChange, className = '' }: RadioType) => (
    <input
        type="radio"
        value={value}
        checked={checked}
        onChange={onChange}
        className={`radio ${className}`}
    />
);
const BulkEmaisPopup: React.FC<BulkEmailsPopupProps> = ({
    open,
    onClose,
    bulkEmailUrlData,
    headers,
    totalData,
    loading,
    programKey,
    kpiFilter,
}) => {
    const [seekersData, setSeekersData] = useState([]);
    const [pageSize, setPageSize] = useState(100);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentStep, setCurrentStep] = useState(BULKEMAIL.SELECTION);
    let url = "";

    const [templateData, setTemplateData] = useState<EmailTriggerOption[]>([]);
    useEffect(() => {
        const fetchCommunicationTemplate = async () => {
            try {
                setIsLoading(true);
                const response = await getCall(`communication/templates?&category=${kpiFilter}&programKey=${programKey}`, undefined, PORTAL);
                if (response.data?.data) {
                    setTemplateData(response.data.data.data);
                }
            } catch (error) {
                console.error("Error fetching communication template:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchCommunicationTemplate();
    }, [kpiFilter, programKey]);
    const [isLoading, setIsLoading] = useState(false);
    const filteredHeaders = headers.filter(header =>
        ['seekerName', 'basicDetails', 'numberOfHDBs', 'averageRating'].includes(header.field)
    );


    const handleBack = () => {
        setCurrentStep(BULKEMAIL.SELECTION);
    };
    const [selectedTrigger, setSelectedTrigger] = useState('');
    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedTrigger(event.target.value);
    };
    const getTemplateConstent = async (templateKey: string, type: string) => {
        try {
            const response = await getCall(`communication/template?templateKey=${templateKey}&templateType=${type}`, undefined, PORTAL);
            return response.data.data.templateContent;
        } catch (error) {
            console.error("Error fetching template content:", error);
            return '';
        }
    };

    const updateUrl = () => {
        url = `${endPoints.registeredSeekerList}?limit=${pageSize}&offset=${(currentPage - 1) * pageSize}`;
        if (bulkEmailUrlData?.programId) url += `&programId=${bulkEmailUrlData.programId}`;
        if (bulkEmailUrlData?.filters) {
            url += `&filters=${JSON.stringify(bulkEmailUrlData.filters)}`;
        }
    }
    const handleViewSeekers = async () => {
        setIsLoading(true);
        try {
            updateUrl();
            const selectedOption = templateData.find(option => option.templateKey === selectedTrigger);
            url += `&communicationListForTemplateKey=${selectedOption?.templateKey}`;
            const response = await getCall(url, undefined, PORTAL);
            setSeekersData(response.data.data.data);
        } catch (error) {
            console.error("Error setting email template:", error);
        } finally {
            setIsLoading(false);
        }
    };
    useEffect(() => {
        if (currentStep === BULKEMAIL.VIEW_SEEKERS) {
            handleViewSeekers();
        }
    }, [currentPage, pageSize]);

    const handleSendMail = async () => {
        setIsLoading(true);
        try {
            updateUrl();
            const selectedOption = templateData.find(option => option.templateKey === selectedTrigger);
            if (selectedOption?.templateKey) {
                const response = await getCall(`${url}&communicationType=${selectedOption.templateType}&communicationTemplateKey=${selectedOption.templateKey}`, undefined, PORTAL);
                notify(BULKEMAIL.toastTitle, response.data.message || BULKEMAIL.toastMessage.SUCCESS, BULKEMAIL.toastType.SUCCESS || "success");
                onClose();
            }
        } catch (error) {
            notify(BULKEMAIL.toastTitle, BULKEMAIL.toastMessage.WARNING, BULKEMAIL.toastType.WARNING || "warning");
            onClose();
        } finally {
            setIsLoading(false);
        }
    }
    const CustomDialogContent = styled(DialogContent)(() => ({
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        gap: '24px',
        width: '100%',
        position: 'relative',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': {
            display: 'none'
        },
        marginTop: '20px',
        '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '48px',
            right: 0,
            width: '120px',
            zIndex: 4,
            height: '120px',
            backgroundImage: currentStep === BULKEMAIL.SELECTION ? `url(${bulkEmailBg})` : 'none',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundSize: 'contain',
            pointerEvents: 'none',
            transform: 'translateX(0)',
        }
    }));
    const StyledDialogTitle = styled(Typography)(() => ({
        color: '#051B46',
        fontWeight: 600,
        fontFamily: 'Noto Sans',
        fontSize: '16px',
        lineHeight: 1.6,
        width: '120px',
    }));
    return (
        <div>
            <Dialog
                open={open}
                onOpenChange={onClose}
                onClose={onClose}
                className={styles.dialogContainer}
            >
                <DialogTitle className={styles.dialogHeader} sx={{ padding: '0px' }}>
                    {currentStep === BULKEMAIL.VIEW_SEEKERS && (
                        <div className={styles.viewSeekersHeader}>
                            <IconButton onClick={handleBack}>
                                <ArrowLeft size={20} color="#051B46" />
                            </IconButton>
                            <StyledDialogTitle>{BULKEMAIL.SEEKERS_LIST}</StyledDialogTitle>
                        </div>
                    )}
                    {currentStep === BULKEMAIL.SELECTION && <StyledDialogTitle>
                        {BULKEMAIL.EMAIL_TRIGGERER}
                    </StyledDialogTitle>}
                    <hr className={currentStep != BULKEMAIL.PREVIEW ? styles.horizontalLine : styles.previewHorizontalLine} />
                    {currentStep === BULKEMAIL.SELECTION && <IconButton onClick={onClose}>
                        <img src={closeIcon} alt="Close" className={styles.closeIcon} />
                    </IconButton>}

                </DialogTitle>
                <CustomDialogContent className={styles.dialogContent} showCloseButton={false}>
                    {isLoading ? (
                        <div className={styles.loaderWrapper}>
                            <Loader type="small" />
                        </div>) : currentStep === BULKEMAIL.SELECTION ? (
                            <>{templateData.length > 0 ? templateData.map((option) => (
                                <FormControlLabel
                                    key={option.id}
                                    value={option.id}
                                    control={
                                        <Radio
                                            value={option.templateKey}
                                            checked={selectedTrigger === option.templateKey}
                                            onChange={handleRadioChange}
                                            className={styles.radioInput}
                                        />
                                    }
                                    label={option.templateName}
                                    className={styles.radioLabel}
                                    sx={{ marginLeft: 0, marginRight: 2, gap: '8px', color: '#051B46', fontWeight: 400, fontFamily: 'Noto Sans' }}
                                />
                            )
                            ) : (
                                <div className={styles.emailIconWrapper}>
                                    <img src={emailIcon} alt="email icon" className={styles.emailIcon} />
                                </div>
                            )}
                            </>
                        ) : (          
                        <DataGridWithPagination
                            headers={filteredHeaders}
                            seekersData={seekersData}
                            totalData={seekersData.length}
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            loading={loading}
                            isheight={true}
                            heightToApplyonGrid="calc(100vh - 365px)"
                            smallSize={true}
                        />
                    )

                    }
                </CustomDialogContent>
                {currentStep !== BULKEMAIL.VIEW_SEEKERS && <DialogFooter className={styles.dialogFooter}>
                    {BULKEMAIL.SELECTION && <DialogActions className={styles.dialogActions}>
                        <button onClick={() => {
                            handleViewSeekers()
                            setCurrentStep(BULKEMAIL.VIEW_SEEKERS);
                        }} className={selectedTrigger ? styles.secondaryButton : styles.disabledButton} disabled={!selectedTrigger}>
                            {BULKEMAIL.VIEW_SEEKERS}
                        </button>
                        {/* <button
                            className={selectedTrigger ? styles.secondaryButton : styles.disabledButton}
                        >
                            {BULKEMAIL.TEST_MAIL}
                        </button> */}
                        <Button onClick={() => { handleSendMail() }} buttonClassName={styles.primaryButton} buttonTextClassName={styles.primaryButtonText} disable={!selectedTrigger}>
                            {`${BULKEMAIL.SEND} mail`}
                        </Button>
                    </DialogActions>}
                </DialogFooter>}
            </Dialog>
        </div>
    );
}
export default BulkEmaisPopup;